import { useEffect, useRef, useState } from 'react';
import { DialogState, DriverStatus, SidebarContentId, SortByField } from '../../../types';
import { deduceDriverStatusesFrom, getDriversUriForDeepLink, parseRoute } from './routeService';
import { useLocation } from 'react-router';
import { useUpdateRoute } from './routeHooks';
import { enumFromEnumValue } from './enumUtils';
import { DriverStatusInRoute } from './types';
import SortDirection from '@rio-cloud/rio-uikit/SortDirection';
import {
    driverStatusesChanged,
    getDriverStatusesRtk,
    getSearchTextRtk,
    getSelectedDriverIdRtk,
    getSortByRtk,
    getSortDirRtk,
    searchTextChanged,
    sortChanged,
    toggleDriverCreationDialog,
} from '../appSlice';
import { changeTab, getContentIdRtk } from '../drivers/sidebar/sidebarSlice';
import { useAppDispatch, useAppSelector } from '../../../configuration/setup/hooks';
import { driverSelectedThunk } from '../../crossSliceThunks';

export const RouteUpdater = (): null => {
    const dispatch = useAppDispatch();

    const currentDriverStatus = useAppSelector(getDriverStatusesRtk);
    const currentSearch = useAppSelector(getSearchTextRtk);
    const currentTab = useAppSelector(getContentIdRtk);
    const routeSearchToPushTo = useAppSelector(getDriversUriForDeepLink);
    const selectedDriverId = useAppSelector(getSelectedDriverIdRtk);
    const selectedSortBy = useAppSelector(getSortByRtk);
    const selectedSortDirString = useAppSelector(getSortDirRtk);

    const selectedSortDir = enumFromEnumValue(SortDirection, selectedSortDirString);
    const { search, pathname } = useLocation();

    // Parse either initial route, or after it has changed by browser navigation, or user input
    const { hasHydratedRouteState, hasPendingLocationSync } = useRouteState(() => {
        const dispatchQueue: Array<() => void> = [];

        const routeSearchParams = parseRoute({
            location: { search, pathname },
        });

        const {
            driverId,
            sortBy,
            sortDir,
            status,
            search: searchFromRoute,
            tab,
            createDriverModal,
        } = routeSearchParams;
        const driverStatusFromRoute = enumFromEnumValue(DriverStatusInRoute, status);
        const driverStatus = driverStatusFromRoute
            ? deduceDriverStatusesFrom(driverStatusFromRoute)
            : [DriverStatus.ACTIVE];
        const nextSortBy = sortBy ?? SortByField.FIELD_LAST_NAME;
        const nextSortDir = sortDir ?? SortDirection.ASCENDING;
        const nextSearch = searchFromRoute ?? '';
        const nextTab = tab ?? SidebarContentId.INFO;

        if (nextSortBy !== selectedSortBy || nextSortDir !== selectedSortDir) {
            dispatchQueue.push(() =>
                dispatch(
                    sortChanged({
                        sortBy: nextSortBy,
                        sortDir: nextSortDir,
                    })
                )
            );
        }

        if (nextSearch !== currentSearch) {
            dispatchQueue.push(() => dispatch(searchTextChanged(nextSearch)));
        }

        if (
            driverStatus.length !== currentDriverStatus.length ||
            driverStatus.some(statusValue => !currentDriverStatus.includes(statusValue))
        ) {
            dispatchQueue.push(() => dispatch(driverStatusesChanged(driverStatus)));
        }

        dispatchQueue.push(() => dispatch(toggleDriverCreationDialog(createDriverModal === DialogState.SHOW)));

        if (nextTab !== currentTab) {
            dispatchQueue.push(() => dispatch(changeTab(nextTab)));
        }

        if (driverId && driverId !== selectedDriverId) {
            dispatchQueue.push(() => dispatch(driverSelectedThunk(driverId)));
        }

        dispatchQueue.forEach(executeDispatch => {
            executeDispatch();
        });
    });

    // Update route whenever an observed store prop changes.
    // The key of the RouteState is the search param key in the URL.
    useUpdateRoute(routeSearchToPushTo, hasHydratedRouteState && !hasPendingLocationSync);

    return null;
};

const useRouteState = (callback: () => void) => {
    const { pathname, search } = useLocation();
    const routeRef = useRef<string | null>(null);
    const [hasHydratedRouteState, setHasHydratedRouteState] = useState(false);
    const routeKey = `${pathname}${search}`;
    const hasPendingLocationSync = routeRef.current !== routeKey;

    useEffect(() => {
        if (routeRef.current === routeKey) {
            return;
        }
        callback();
        routeRef.current = routeKey;
        setHasHydratedRouteState(true);
    }, [routeKey, callback]);

    return {
        hasHydratedRouteState,
        hasPendingLocationSync,
    };
};
